import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, TrendingUp, TrendingDown, Activity, UserPlus, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

interface PlayerStatisticsCompactProps {
  playerId: string;
}

interface PlayerStatistics {
  global_matches_played: number;
  global_win_rate: number;
  left_win_rate: number;
  right_win_rate: number;
  all_time_best_partner_name: string | null;
  all_time_best_partner_matches: number | null;
  all_time_best_partner_win_rate: number | null;
  current_spree: number;
  best_spree: number;
  worst_spree: number;
  games_per_month: number;
  games_per_week: number;
}

export default function PlayerStatisticsCompact({ playerId }: PlayerStatisticsCompactProps) {
  const { t } = useTranslation();
  const { data: statistics, isLoading, error } = useQuery<PlayerStatistics>({
    queryKey: [`/api/players/${playerId}/statistics`],
    enabled: !!playerId,
  });

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse p-4">
        <Skeleton className="h-4 w-1/2 mx-auto" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !statistics) {
    return (
      <div className="p-4 text-center text-muted-foreground bg-gray-50 dark:bg-muted rounded-xl border border-gray-100 dark:border-border text-sm">
        <Activity className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
        <p>{t('players.stats.noStats')}</p>
      </div>
    );
  }

  const formatWinRate = (rate: number | null) => {
    if (rate === null || rate === undefined) return "0%";
    return `${Math.round(rate * 100)}%`;
  };

  const currentSpreeColor = statistics.current_spree > 0 ? 'text-blue-600' : (statistics.current_spree < 0 ? 'text-red-500' : 'text-gray-500');

  return (
    <div className="grid grid-cols-2 gap-3 mt-3 animate-in fade-in slide-in-from-top-2 duration-300">
      {/* Global Win Rate */}
      <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border flex flex-col justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
          <Trophy className="w-3.5 h-3.5" />
          {t('players.stats.globalWinRate')}
        </div>
        <div>
          <div className="text-xl font-black text-foreground leading-none mb-1.5">
            {formatWinRate(statistics.global_win_rate)}
          </div>
          <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-blue-600 h-1.5 rounded-full"
              style={{ width: `${(statistics.global_win_rate || 0) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Side Preferences */}
      <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border flex flex-col justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
           {t('players.stats.side.title')}
        </div>
        <div className="flex justify-between items-center gap-2">
            <div className="flex-1 bg-white dark:bg-card border border-gray-100 dark:border-border rounded text-center py-1">
                <div className="text-[10px] text-muted-foreground font-medium uppercase">{t('players.stats.side.left')}</div>
                <div className="text-sm font-bold text-foreground">{formatWinRate(statistics.left_win_rate)}</div>
            </div>
            <div className="flex-1 bg-white dark:bg-card border border-gray-100 dark:border-border rounded text-center py-1">
                <div className="text-[10px] text-muted-foreground font-medium uppercase">{t('players.stats.side.right')}</div>
                <div className="text-sm font-bold text-foreground">{formatWinRate(statistics.right_win_rate)}</div>
            </div>
        </div>
      </div>

      {/* Best Partner */}
      <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border flex flex-col items-center justify-center text-center">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 self-start">
          <UserPlus className="w-3.5 h-3.5" />
          {t('players.stats.bestPartner')}
        </div>
        <div className="w-full">
          <div className="text-lg font-bold text-foreground truncate px-1">
            {statistics.all_time_best_partner_name ? statistics.all_time_best_partner_name.split(' ')[0] : '-'}
          </div>
          <div className="text-[11px] text-blue-600 font-medium mt-1">
             {statistics.all_time_best_partner_name ? (
                <>
                  {statistics.all_time_best_partner_matches} {t('players.tab.stats.streak.matches', 'matches')} • {formatWinRate(statistics.all_time_best_partner_win_rate || 0)}
                </>
             ) : (
                t('players.stats.none')
             )}
          </div>
        </div>
      </div>

      {/* Current Form */}
      <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border flex flex-col justify-between">
         <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
          <Zap className="w-3.5 h-3.5" />
          {t('players.stats.streak.title')}
        </div>
        <div className="flex items-center justify-between mt-1">
             <div className="flex-1 text-center">
                 <div className="text-[10px] text-muted-foreground font-medium uppercase">{t('players.stats.streak.current')}</div>
                 <div className={`text-xs font-bold ${currentSpreeColor}`}>
                    {statistics.current_spree > 0 ? '+' : ''}{statistics.current_spree}
                 </div>
             </div>
             <div className="w-px h-6 bg-gray-200 dark:bg-border"></div>
             <div className="flex-1 text-center">
                 <div className="text-[10px] text-muted-foreground font-medium uppercase">{t('players.stats.streak.best')}</div>
                 <div className="text-xs font-bold text-blue-600">
                    +{statistics.best_spree}
                 </div>
             </div>
             <div className="w-px h-6 bg-gray-200 dark:bg-border"></div>
             <div className="flex-1 text-center">
                 <div className="text-[10px] text-muted-foreground font-medium uppercase">{t('players.stats.streak.worst')}</div>
                 <div className="text-xs font-bold text-red-500">
                    -{statistics.worst_spree}
                 </div>
             </div>
        </div>
      </div>
    </div>
  );
}
